/**
 * Exporta as tabelas da tela "Resumo" do relatório de inventário para um
 * arquivo .xlsx. Dados e colunas: `reportTables/` (ver TABELAS_RELATORIO_CANAIS.md).
 */

import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { CalculosInventario, Inventario } from '../types';
import {
  buildCalculoPorPessoaExcelRows,
  buildComposicaoExcelSheet,
  buildDespesasInformativasExcelSheet,
  buildItensForaEscopoExcelSheet,
  buildModoPartilhaExcelSheet,
  buildPartilhaMatrixExcelSheet,
  buildValuationBasesExcelSheet,
  buildCalculoPorPessoaWorksheet,
  sheetDataToWorksheet,
} from '../reportTables';

export interface ExportResumoToExcelParams {
  inventario: Inventario;
  calculos: CalculosInventario;
  custosTributosPorPessoa?: Record<
    string,
    { registroImoveis: number; honorariosAdvocaticios: number }
  >;
  /** Nome de arquivo opcional (sem extensão). */
  fileName?: string;
}

function sanitizeFileName(name: string): string {
  return name
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9-_]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 80) || 'inventario';
}

/**
 * Gera e dispara o download de um arquivo .xlsx com as tabelas do Resumo.
 */
export function exportResumoToExcel(params: ExportResumoToExcelParams): void {
  const {
    inventario,
    calculos,
    custosTributosPorPessoa = {},
    fileName,
  } = params;

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(
    wb,
    sheetDataToWorksheet(buildComposicaoExcelSheet(inventario, calculos)),
    'Composição',
  );
  XLSX.utils.book_append_sheet(
    wb,
    sheetDataToWorksheet(buildValuationBasesExcelSheet(inventario)),
    'Bases avaliação',
  );
  XLSX.utils.book_append_sheet(
    wb,
    sheetDataToWorksheet(
      buildPartilhaMatrixExcelSheet(calculos, custosTributosPorPessoa),
    ),
    'Partilha',
  );

  const calculoPorPessoa = buildCalculoPorPessoaExcelRows(
    inventario,
    calculos,
    custosTributosPorPessoa,
  );
  if (calculoPorPessoa) {
    XLSX.utils.book_append_sheet(
      wb,
      buildCalculoPorPessoaWorksheet(
        calculoPorPessoa.matrix,
        calculoPorPessoa.numPeople,
      ),
      'Cálculo por pessoa',
    );
  }

  const modoPartilhaSheet = buildModoPartilhaExcelSheet(inventario, calculos);
  if (modoPartilhaSheet) {
    XLSX.utils.book_append_sheet(
      wb,
      sheetDataToWorksheet(modoPartilhaSheet),
      'Modo de partilha',
    );
  }

  const informativasSheet = buildDespesasInformativasExcelSheet(calculos);
  if (informativasSheet) {
    XLSX.utils.book_append_sheet(
      wb,
      sheetDataToWorksheet(informativasSheet),
      'Despesas informativas',
    );
  }

  const ignoradasSheet = buildItensForaEscopoExcelSheet(calculos);
  if (ignoradasSheet) {
    XLSX.utils.book_append_sheet(
      wb,
      sheetDataToWorksheet(ignoradasSheet),
      'Itens fora do escopo',
    );
  }

  const buffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  const blob = new Blob([buffer], {
    type:
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });

  const baseName =
    fileName ||
    `Resumo_Inventario_${sanitizeFileName(inventario.decujus?.nome || 'inventario')}`;
  saveAs(blob, `${baseName}.xlsx`);
}
